import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { PMDialog } from "../../utils/PMDialog";
import { fetchAccountingResultReportAccountDetail } from "../../../api/accounting";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("nb-NO", { dateStyle: "medium" }).format(new Date(value));
  } catch {
    return value;
  }
};

const SOURCE_LABEL_KEYS = {
  voucher: "accounting.result_report.account_detail.source_voucher",
  supplierinvoice: "accounting.result_report.account_detail.source_supplier",
  customerinvoice: "accounting.result_report.account_detail.source_customer_invoice",
};

function buildRows(detail) {
  if (!detail) return [];

  const voucherRows = (detail.voucher_postings || []).map((row) => ({
    key: `voucher-${row.id}`,
    source: row.source,
    number: row.voucher_number,
    date: row.date,
    description: row.description,
    amount: row.amount,
    project_name: row.project_name,
    link: row.voucher_url,
    externalLink: row.tripletex_url,
  }));

  const supplierRows = (detail.supplierinvoice_postings || []).map((row) => ({
    key: `supplier-${row.id}`,
    source: row.source,
    number: row.invoice_number,
    date: row.date,
    description: row.description,
    amount: row.amount,
    project_name: row.project_name,
    link: row.supplierinvoice_url,
    externalLink: row.tripletex_url,
  }));

  const customerRows = (detail.customer_invoices || []).map((row) => ({
    key: `customer-${row.id}`,
    source: row.source,
    number: row.invoice_number,
    date: row.date,
    description: row.description,
    amount: row.amount,
    project_name: row.customer_name,
    link: row.customerinvoice_url,
    externalLink: row.tripletex_url,
  }));

  return [...voucherRows, ...supplierRows, ...customerRows].sort((a, b) => {
    const dateCmp = (a.date || "").localeCompare(b.date || "");
    if (dateCmp !== 0) return dateCmp;
    return (a.number || "").localeCompare(b.number || "");
  });
}

export const AccountDetailDialog = ({
  open,
  onClose,
  accountNumber,
  accountName,
  year,
  formatCurrency,
}) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !accountNumber) {
      setDetail(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchAccountingResultReportAccountDetail({
          accountNumber,
          year,
        });
        if (cancelled) return;
        if (result.error) {
          setDetail(null);
          setError(result.data?.errors?.[0] || t("accounting.result_report.account_detail.load_error"));
        } else {
          setDetail(result.data);
        }
      } catch {
        if (!cancelled) {
          setError(t("accounting.result_report.account_detail.load_error"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, accountNumber, year, t]);

  const rows = buildRows(detail);
  const titleAccount = detail?.account_name || accountName || accountNumber;
  const dialogTitle = t("accounting.result_report.account_detail.title", {
    account: titleAccount,
    number: accountNumber,
  });

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={dialogTitle}
      size="5xl"
      isLoading={loading}
      closeOnOutsideClick
    >
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {detail && !error && (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <p className="text-sm text-slate-500">{t("accounting.result_report.account_number")}</p>
              <p className="text-lg font-semibold tabular-nums text-slate-900">
                {detail.account_number} — {detail.account_name}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">{t("accounting.result_report.amount")}</p>
              <p className="text-2xl font-bold tabular-nums text-slate-950">
                {formatCurrency(detail.total_amount)}
              </p>
              <p className="mt-1 text-xs text-slate-500">
                {t("accounting.result_report.account_detail.posting_count", {
                  count: detail.posting_count || 0,
                })}
              </p>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-slate-600">
              {t("accounting.result_report.account_detail.no_rows")}
            </p>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-slate-500">
                  <tr>
                    <th className="px-4 py-3">{t("accounting.result_report.account_detail.voucher_number")}</th>
                    <th className="px-3 py-3">{t("accounting.result_report.account_detail.date")}</th>
                    <th className="px-3 py-3">{t("accounting.result_report.account_detail.description")}</th>
                    <th className="px-3 py-3 text-right">{t("accounting.result_report.amount")}</th>
                    <th className="px-3 py-3">{t("accounting.result_report.account_detail.project")}</th>
                    <th className="px-3 py-3">{t("accounting.result_report.account_detail.source")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr key={row.key} className="hover:bg-slate-50/70">
                      <td className="px-4 py-2 font-medium tabular-nums text-slate-900">
                        {row.link ? (
                          <Link to={row.link} className="text-blue-700 hover:text-blue-800" onClick={onClose}>
                            {row.number || "—"}
                          </Link>
                        ) : (
                          row.number || "—"
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatDate(row.date)}</td>
                      <td className="px-3 py-2 max-w-xs truncate" title={row.description}>
                        {row.description || "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                        {formatCurrency(row.amount)}
                      </td>
                      <td className="px-3 py-2 text-slate-700">{row.project_name || "—"}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {t(SOURCE_LABEL_KEYS[row.source] || row.source)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {detail.methodology_note && (
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900">
              <div className="flex items-start gap-2">
                <InformationCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-blue-500" />
                <p>{detail.methodology_note}</p>
              </div>
            </div>
          )}

          {(detail.reconciliation_hints || []).length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-900">
                {t("accounting.result_report.account_detail.reconciliation_title")}
              </h4>
              <ul className="list-disc space-y-1 pl-5 text-sm text-slate-600">
                {detail.reconciliation_hints.map((hint) => (
                  <li key={hint}>{hint}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </PMDialog>
  );
};
